import { useState } from 'react';
import { Table, ButtonGroup, Dropdown } from 'semantic-ui-react';
import { Button } from '~/components';
import { updateRfi, deleteRfi } from '~/api/requestForInformation';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

type RfiRowProps = {
  rfi: {
    id: string;
    name: string;
    description: string;
    isLive: boolean;
    sentAt: string;
  };
  refreshList: () => void;
  edit: (rfi: RfiRowProps['rfi']) => void;
};

const RfiRow = ({ rfi, refreshList, edit }: RfiRowProps) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const { name, description, isLive, sentAt } = rfi;
  const status = isLive
    ? 'Sent ' + format(new Date(sentAt), 'M/dd/yyyy')
    : 'Pending';

  const handleSend = async () => {
    try {
      if (isProcessing) return; // Prevent multiple clicks
      setIsProcessing(true);
      const today = new Date();
      const sentAt = today.toISOString().slice(0, 10);
      await toast.promise(
        updateRfi(rfi.id, { is_live: true, sent_at: sentAt }),
        {
          loading: 'Sending RFI...',
          success: 'RFI sent!',
          error: 'Failed to send RFI',
        }
      );
      refreshList();
      setIsProcessing(false);
    } catch (error) {
      console.error('Failed to send RFI:', error);
      setIsProcessing(false);
    }
  };

  const handleRecall = async () => {
    try {
      if (isProcessing) return; // Prevent multiple clicks
      setIsProcessing(true);
      await toast.promise(
        updateRfi(rfi.id, { is_live: false, sent_at: null }),
        {
          loading: 'Recalling RFI...',
          success: 'RFI recalled!',
          error: 'Failed to recall RFI',
        }
      );
      refreshList();
      setIsProcessing(false);
    } catch (error) {
      console.error('Failed to recall RFI:', error);
      setIsProcessing(false);
    }
  };

  const handleEdit = () => {
    edit(rfi);
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this RFI?')) return; // TODO: use a modal instead of window.confirm
    try {
      await toast.promise(
        deleteRfi(rfi.id),
        {
          loading: 'Deleting RFI...',
          success: 'RFI deleted!',
          error: 'Failed to delete RFI',
        }
      );
      refreshList();
    } catch (error) {
      console.error('Failed to delete RFI:', error);
    }
  };

  return (
    <Table.Row>
      <Table.Cell style={{whiteSpace: 'nowrap'}}>{name}</Table.Cell>
      <Table.Cell>{description}</Table.Cell>
      <Table.Cell style={{whiteSpace: 'nowrap'}}>{status}</Table.Cell>
      <Table.Cell>
        <ButtonGroup primary>
          { isLive ? 
            <Button onClick={handleRecall}>Recall</Button> :
            <Button onClick={handleSend}>Send</Button>
          }
          <Dropdown
            className='button icon'
            trigger={<></>}
          >
            <Dropdown.Menu>
              <Dropdown.Item onClick={handleEdit}>Edit</Dropdown.Item>
              <Dropdown.Item onClick={handleDelete}>Delete</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </ButtonGroup>
      </Table.Cell>
    </Table.Row>
  );
};

export default RfiRow;